<script lang="ts" module>
  interface DeriveSideNavStateOptions {
    groupIndex: number
    furthestStepNodeReached: StepNode
  }

  export function getSideNavGroupState({
    groupIndex,
    furthestStepNodeReached,
  }: DeriveSideNavStateOptions): NavItemState {
    const furthestGroupIdx = furthestStepNodeReached.groupIndex
    const isGroupComplete = groupIndex < furthestGroupIdx

    if (isGroupComplete) return "review"
    if (groupIndex === furthestGroupIdx) return "in-progress"
    return "disabled"
  }
</script>

<script lang="ts">
  import { getNavContext } from "$lib/context"
  import type { NavGroup, NavItemState, StepNode } from "$lib/nav"
  import { Check, ChevronRight } from "@tastyworks/icons-2.0"
  import { Accordion } from "@tastyworks/ui-library"
  import { _ } from "svelte-i18n"
  import { slide } from "svelte/transition"
  import { circle, line, text } from "."
  import SideNavGroupItem from "./SideNavGroupItem.svelte"

  interface SideNavGroupProps {
    navGroup: NavGroup
    groupIndex: number
    isGroupOpen: boolean
  }

  let {
    navGroup,
    groupIndex,
    isGroupOpen = $bindable(),
  }: SideNavGroupProps = $props()
  const nav = getNavContext()

  const visibleNavSections = $derived(
    navGroup.items.filter((navSection) => !navSection.hidden)
  )
  const groupState = $derived(
    getSideNavGroupState({
      groupIndex,
      furthestStepNodeReached: nav.furthestStepNodeReached,
    })
  )
  const disabled = $derived(groupState === "disabled")
  let open: boolean = $state(false)

  const groupId = navGroup.id
</script>

<Accordion.Item
  value={groupIndex.toString()}
  class="
		group
		border-b
		border-divider-horizontal-end
		bg-general-secondary-surface
		md:border-none
		md:bg-general-primary-background"
  aria-disabled={disabled}
  data-testid="nav-group-{groupId}"
  role="menuitem"
>
  <div class="flex">
    <Accordion.Trigger
      bind:open
      class={text({
        size: "large",
        state: groupState,
        className: [
          "gap-3",
          "px-4",
          "py-3",
          "group-aria-disabled:pointer-events-none",
          "data-[state=closed]:bg-general-primary-surface",
          "md:mx-2",
          "md:my-1",
          "md:rounded",
          "md:p-2",
          "md:data-[state=closed]:bg-transparent",
          "md:data-[state=open]:bg-interaction-control-selected",
        ],
      })}
      tabindex={disabled ? -1 : 0}
      aria-controls="{groupId}-nav-menu"
      aria-haspopup="true"
      data-nav-state={groupState}
    >
      <div
        class={circle({
          size: "large",
          state: groupState,
          className: "relative",
        })}
      >
        {#if groupState === "review" && groupId !== "review"}
          <Check class="w-5" />
        {:else}
          {groupIndex + 1}
        {/if}
        {#if open}
          <div
            class={line({ size: "large", disabled })}
            out:slide={{ delay: 200, duration: 100 }}
            in:slide={{ duration: 50 }}
          ></div>
        {/if}
      </div>
      <span>{$_(`${groupId}.navLabel`)}</span>
      <div class="flex-1"></div>
      <ChevronRight
        slot="icon"
        class="w-5 group-aria-disabled:hidden group-data-[state=open]:rotate-90 md:hidden"
      />
    </Accordion.Trigger>
  </div>
  <Accordion.Content class="!fill-mode-none" role="presentation">
    <ul
      class="relative z-10 flex flex-col gap-5 px-4 py-3"
      role="menu"
      id="{groupId}-nav-menu"
    >
      {#each visibleNavSections as navSection (navSection.id)}
        <SideNavGroupItem
          {navSection}
          {navGroup}
          {groupState}
          bind:isGroupOpen
        />
      {/each}
    </ul>
  </Accordion.Content>
</Accordion.Item>
